import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  ButtonGroup,
  Button, Select, MenuItem, FormControl, InputLabel,
} from "@mui/material";
import {
  LocationOn,
  People,
  TrendingUp,
  Sort,
  SortByAlpha,
  TrendingDown,
} from "@mui/icons-material";
import {mapRegionDicts} from "../../../hooks/mapRegionDicts";

const SummaryRegisteredData = ({
  beneficiaryData,
  selectedVariable,
  selectedCity,
  tableLock,
  search,
  selectedState,
  totalPopulationTitle
}) => {
  const [sortOption, setSortOption] = useState("default");
  const [sortDirection, setSortDirection] = useState("asc");
  const [stateFilter, setStateFilter] = useState("Todos");
  const isDashboard = window.location.pathname.includes("dashboard");
  const isReports = window.location.pathname.includes("reports");

  const allRegionStates = mapRegionDicts[selectedState?.toLowerCase()]

  const sumMacroData = (data) => {
    return data.slice(4).reduce((acc, item) => acc + Number(item), 0);
  };

  const sumAllData = (data) => {
    return data.reduce((acc, item) => {
      const count = sumMacroData(item);
      return acc + count;
    }, 0);
  };

  const regionNames = {
    total: "Municípios",
    valuesMicro: "Micro-regiões",
    valuesMacro: "Macro-regiões",
    valuesHealthRegion: "Regiões de Saúde",
    valuesState: "Estados",
  };

  const regionName = !tableLock ? regionNames[selectedVariable] : "Município";

  const getRegionState = (regionName) => {
    const match = regionName?.match(/ - ([A-Z]{2})$/);
    return match ? match[1] : null;
  };

  const filterByState = (data) => {
    if (!selectedState || !data) return data;

    if (!allRegionStates) {
      return data.filter((item) => {
        const regionName = item[0];
        return getRegionState(regionName) === selectedState;
      });
    } else{
      return data.filter((item) => {
        const regionName = item[0]
        const regionState = getRegionState(regionName)
        return allRegionStates.includes(regionState)
      })
    }
  };

  const getFilteredData = () => {
    if (!beneficiaryData) return [];
    if (!selectedVariable) selectedVariable = "total";

    if (selectedVariable === "total" || tableLock) {
      const cityData = filterByState(beneficiaryData["valuesCity"]);

      if (selectedCity && Array.isArray(selectedCity) && search) {
        const filteredCities = selectedCity.find(
          (item) => item[0]?.split(" - ")[0] === search
        );

        if (filteredCities && !tableLock) {
          const filtered = cityData.find(
            (item) => item[0] === filteredCities[0]
          );
          return filtered
            ? [[filtered[0], sumMacroData(filtered), filtered[3]]]
            : [];
        }
      }

      if (search && !tableLock) {
        const filtered = cityData.find((item) =>
          item[0]?.toLowerCase().includes(search.toLowerCase())
        );
        return filtered
          ? [[filtered[0], sumMacroData(filtered), filtered[3]]]
          : [];
      }

      let filteredRegionData = cityData;

      if ((stateFilter !== "Todos") && selectedVariable !== "valuesState") {
        filteredRegionData = filteredRegionData.filter(
          (item) => item[1] === stateFilter
        );
      }

      return filteredRegionData.map((item) => {
        const name = item[0];
        const count = sumMacroData(item);
        const cityHq = item[3];
        return [name, count, cityHq];
      });
    }

    const regionData = beneficiaryData[selectedVariable];
    if (!regionData) return [];

    if (search && !tableLock) {
      const filtered = regionData.find((item) =>
        item[0]?.toLowerCase().includes(search.toLowerCase())
      );
      return filtered
        ? [[filtered[0], sumMacroData(filtered), filtered[3]]]
        : [];
    }

    let filteredRegionData = regionData;

    if ((stateFilter !== "Todos") && selectedVariable !== "valuesState") {
      filteredRegionData = filteredRegionData.filter(
        (item) => item[1] === stateFilter
      );
    }
    return filteredRegionData.map((item) => {
      const name = item[0];
      const count = sumMacroData(item);
      const cityHq = item[3];
      return [name, count, cityHq];
    });
  };

  const sortData = (data) => {
    if (sortOption === "alphabetical") {
      return [...data].sort((a, b) => {
        const result = a[0].localeCompare(b[0]);
        return sortDirection === "asc" ? result : -result;
      });
    } else if (sortOption === "population") {
      return [...data].sort((a, b) => {
        const result = b[1] - a[1];
        return sortDirection === "asc" ? -result : result;
      });
    }
    return data;
  };

  const getSortIcon = () => {
    if (sortOption === "alphabetical") {
      return <SortByAlpha fontSize="small" />;
    } else if (sortOption === "population") {
      return sortDirection === "asc" ? (
        <TrendingUp fontSize="small" />
      ) : (
        <TrendingDown fontSize="small" />
      );
    }
    return <Sort fontSize="small" />;
  };

  const handleSortChange = (newSortOption) => {
    if (newSortOption === sortOption && newSortOption !== "default") {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortOption(newSortOption);
      if (newSortOption === "population") {
        setSortDirection("desc");
      } else if (newSortOption === "alphabetical") {
        setSortDirection("asc");
      }
    }
  };

  const filteredData = sortData(getFilteredData());

  const totalPopulation = sumAllData(
    (selectedVariable === "total" || tableLock) && selectedState
      ? filterByState(beneficiaryData?.valuesCity || [])
      : beneficiaryData?.valuesCity || []
  );

  const maxCount = Math.max(...filteredData.map(([, count]) => count || 0), 1);

  const formatNumber = (num) => {
    return new Intl.NumberFormat("pt-BR").format(num);
  };

  const getPercentage = (count) => {
    if (isNaN(count) || isNaN(totalPopulation) || totalPopulation === 0) {
      return "0%";
    }
    const percentage = (count / totalPopulation) * 100;
    return percentage < 0.01 ? "~0.01%" : `${percentage.toFixed(2)}%`;
  };

  // Função para calcular a cor baseada no percentual da população
  const getPercentageColor = (count) => {
    const percentage = (count / totalPopulation) * 100;
    if (percentage > 15) return "#047857"; // Verde escuro para valores mais altos
    if (percentage > 5) return "#059669"; // Verde médio
    return "#10b981"; // Verde claro para valores mais baixos
  };

  return (
    <div className="w-full h-full flex flex-col">
      {filteredData.length > 0 && (
        <Box className="mb-4 p-4 bg-gradient-to-r from-emerald-50 to-green-50 rounded-lg border border-emerald-200 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
            <div className="p-3 bg-white rounded-lg shadow-sm">
              <Typography
                variant="subtitle2"
                className="text-gray-500 mb-1 flex items-center justify-center gap-1"
              >
                <People fontSize="small" />
                {totalPopulationTitle ? totalPopulationTitle: "População Total"}
              </Typography>
              <Typography
                variant="h5"
                className="font-semibold text-emerald-700"
              >
                {formatNumber(totalPopulation)}
              </Typography>
            </div>
            <div className="p-3 bg-white rounded-lg shadow-sm">
              <Typography
                variant="subtitle2"
                className="text-gray-500 mb-1 flex items-center justify-center gap-1"
              >
                <LocationOn fontSize="small" />
                {regionName}
              </Typography>
              <Typography
                variant="h5"
                className="font-semibold text-emerald-700"
              >
                {filteredData.length}
              </Typography>
            </div>
            <div className="p-3 bg-white rounded-lg shadow-sm">
              <Typography
                variant="subtitle2"
                className="text-gray-500 mb-1 flex items-center justify-center gap-1"
              >
                <TrendingUp fontSize="small" />
                Maior Concentração
              </Typography>
              <Typography
                variant="h5"
                className="font-semibold text-emerald-700"
              >
                {formatNumber(maxCount)}
              </Typography>
            </div>
          </div>
        </Box>
      )}

      <Box className="mb-4 flex  flex-col xl:flex-row justify-center items-center xl:justify-between xl:items-center gap-4 bg-white p-4 rounded-lg shadow-sm">
        <Typography
          variant="h6"
          className="text-gray-700 font-semibold flex items-center gap-2"
        >
          <LocationOn className="text-emerald-600" />
          Dados Registrados
        </Typography>
        {(
          ((!tableLock && (selectedVariable !== "total" || (!isDashboard && !isReports))) && selectedVariable !== "valuesState")
        ) && (
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Estado</InputLabel>
            <Select
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
              variant="outlined"
              label="Estado"
            >
              <MenuItem value="Todos">Todos</MenuItem>
              {beneficiaryData?.valuesState?.map((state) => (
                <MenuItem key={state[0]} value={state[0]}>
                  {state[0]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <Box className="flex gap-2 w-full xl:w-auto justify-center xl:justify-end">
          <ButtonGroup
            variant="outlined"
            size="small"
            className="w-full xl:w-auto"
          >
            <Button
              onClick={() => handleSortChange("default")}
              variant={sortOption === "default" ? "contained" : "outlined"}
              sx={{
                backgroundColor:
                  sortOption === "default" ? "#10b981" : "transparent",
                color: sortOption === "default" ? "white" : "#10b981",
                borderColor: "#10b981",
                textTransform: "none",
                fontWeight: 500,
                flex: { xs: 1, xl: "none" },
                "&:hover": {
                  backgroundColor:
                    sortOption === "default" ? "#059669" : "#dcfce7",
                  borderColor: "#10b981",
                },
              }}
            >
              <Sort fontSize="small" sx={{ mr: 1 }} />
              Padrão
            </Button>
            <Button
              onClick={() => handleSortChange("alphabetical")}
              variant={sortOption === "alphabetical" ? "contained" : "outlined"}
              sx={{
                backgroundColor:
                  sortOption === "alphabetical" ? "#10b981" : "transparent",
                color: sortOption === "alphabetical" ? "white" : "#10b981",
                borderColor: "#10b981",
                textTransform: "none",
                fontWeight: 500,
                flex: { xs: 1, xl: "none" },
                "&:hover": {
                  backgroundColor:
                    sortOption === "alphabetical" ? "#059669" : "#dcfce7",
                  borderColor: "#10b981",
                },
              }}
            >
              <SortByAlpha fontSize="small" sx={{ mr: 1 }} />
              A-Z
              {sortOption === "alphabetical" && (
                <Box sx={{ ml: 1, fontSize: "16px", fontWeight: "bold" }}>
                  {sortDirection === "asc" ? "↑" : "↓"}
                </Box>
              )}
            </Button>
            <Button
              onClick={() => handleSortChange("population")}
              variant={sortOption === "population" ? "contained" : "outlined"}
              sx={{
                backgroundColor:
                  sortOption === "population" ? "#10b981" : "transparent",
                color: sortOption === "population" ? "white" : "#10b981",
                borderColor: "#10b981",
                textTransform: "none",
                fontWeight: 500,
                flex: { xs: 1, xl: "none" },
                "&:hover": {
                  backgroundColor:
                    sortOption === "population" ? "#059669" : "#dcfce7",
                  borderColor: "#10b981",
                },
              }}
            >
              {sortOption === "population" ? (
                getSortIcon()
              ) : (
                <TrendingUp fontSize="small" />
              )}
              <Box sx={{ ml: 1 }}>
                População
                {sortOption === "population" && (
                  <Box
                    component="span"
                    sx={{ ml: 1, fontSize: "16px", fontWeight: "bold" }}
                  >
                    {sortDirection === "desc" ? "↓" : "↑"}
                  </Box>
                )}
              </Box>
            </Button>
          </ButtonGroup>
        </Box>
      </Box>

      <TableContainer
          component={Paper}
          elevation={2}
          sx={{
            flex: 1,
            overflowY: "auto",
            border: "1px solid #e5e7eb",
            borderRadius: "0.75rem",
            boxShadow:
                "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
            transition: "all 0.3s ease-in-out",
          }}
      >
      <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                className="bg-gray-50 border-b"
                sx={{
                  fontWeight: 600,
                  color: "#374151",
                  padding: "12px 16px",
                  backgroundColor:
                    sortOption === "alphabetical" ? "#dcfce7" : "#f9fafb",
                }}
              >
                <div className="flex items-center gap-2">
                  <LocationOn
                    fontSize="small"
                    className={
                      sortOption === "alphabetical" ? "text-emerald-600" : ""
                    }
                  />
                  <span>{!regionName ? "Região" : regionName}</span>
                  {sortOption === "alphabetical" && (
                    <Box sx={{ ml: 1, color: "#10b981", fontSize: "14px" }}>
                      {sortDirection === "asc" ? "↑" : "↓"}
                    </Box>
                  )}
                </div>
              </TableCell>
              <TableCell
                align="right"
                className="bg-gray-50 border-b"
                sx={{
                  fontWeight: 600,
                  color: "#374151",
                  padding: "12px 16px",
                  backgroundColor:
                    sortOption === "population" ? "#dcfce7" : "#f9fafb",
                }}
              >
                <div className="flex items-center gap-2 justify-end">
                  <People
                    fontSize="small"
                    className={
                      sortOption === "population" ? "text-emerald-600" : ""
                    }
                  />
                  <span>Total</span>
                  {sortOption === "population" && (
                    <Box sx={{ ml: 1, color: "#10b981", fontSize: "14px" }}>
                      {sortDirection === "desc" ? "↓" : "↑"}
                    </Box>
                  )}
                </div>
              </TableCell>
              <TableCell
                align="right"
                className="bg-gray-50 border-b"
                sx={{
                  color: "#374151",
                  fontWeight: 600,
                  padding: "12px 16px",
                }}
              >
                <div className="flex items-center gap-2 justify-end">
                  <TrendingUp fontSize="small" />
                  <span>%</span>
                </div>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody
            sx={{
              "& .MuiTableRow-root": { transition: "all 0.3s ease-in-out" },
            }}
          >
            {filteredData.length > 0 ? (
              filteredData.map(([name, count, cityHq], index) => (
                <TableRow
                  key={index}
                  hover
                  className={index % 2 === 0 ? "" : "bg-gray-50"}
                  sx={{
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      backgroundColor: "#dcfce7",
                      transform: "translateY(-1px)",
                      boxShadow: "0 2px 4px -1px rgba(0, 0, 0, 0.1)",
                    },
                  }}
                >
                  <TableCell
                    sx={{
                      padding: "10px 16px",
                      borderLeft: `3px solid ${getPercentageColor(count)}`,
                    }}
                  >
                    <Typography
                      variant="body2"
                      className="font-medium text-gray-800"
                    >
                      {name}
                    </Typography>
                    {selectedVariable !== "valuesState" && cityHq && (
                      <Typography
                        variant="caption"
                        className="text-emerald-600 italic"
                      >
                        Sede: {cityHq}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ padding: "10px 16px" }}>
                    <Typography
                      variant="body2"
                      className="font-semibold text-gray-800"
                    >
                      {formatNumber(count)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ padding: "10px 16px" }}>
                    <Chip
                      label={getPercentage(count)}
                      size="small"
                      sx={{
                        fontSize: "0.75rem",
                        height: "22px",
                        backgroundColor: "#f3f4f6",
                        color: getPercentageColor(count),
                        fontWeight: 500,
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={3}
                  align="center"
                  sx={{
                    padding: "32px 16px",
                    color: "#6b7280",
                  }}
                >
                  <div className="flex flex-col items-center gap-2">
                    <LocationOn
                      sx={{ fontSize: 40 }}
                      className="text-gray-300"
                    />
                    <Typography variant="body1" className="text-gray-500">
                      Nenhum dado encontrado
                    </Typography>
                    <Typography variant="body2" className="text-gray-400">
                      Tente selecionar uma região diferente
                    </Typography>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default SummaryRegisteredData;